/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useMemo, useLayoutEffect, useCallback, useEffect } from "react";

import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridColDef,
    useGridApiRef,
    GridInitialState,
} from "@mui/x-data-grid";

import { customDataGridScrollbarStyle, generateUuidNative } from "../../utils/Functions";
import { useAppDispatch } from "../../app/hooks";
import { AppDispatch, store } from "../../app/store";
import { addDataTable } from "./dataTablesSlice";
import { Nullable } from "../../utils/Interfaces";

export interface DataTableProps {
    data: any[];
    columns: GridColDef<any, any, any>[];
    children?: JSX.Element | JSX.Element[];
    height: number;
    cellEditStart?: (params: any, event: any) => void;
    cellEditStop?: (params: any, event: any) => void;
    tableName: string;
    allowRowSelected?: boolean;
}

const DataTable = (props: DataTableProps) => {
    const { data, columns, children, height, cellEditStart, cellEditStop, tableName, allowRowSelected = true } = props;

    const [filterButtonEl, setFilterButtonEl] = useState<Nullable<any>>(null);
    const [initialState, setInitialState] = useState<GridInitialState>();

    const dispatch: AppDispatch = useAppDispatch();
    const apiRef = useGridApiRef();

    const saveSnapshot = useCallback(() => {
        if (apiRef?.current?.exportState) {
            const currentState = apiRef.current.exportState();
            dispatch(addDataTable({ [tableName]: currentState }));
        }
    }, [apiRef]);

    useLayoutEffect(() => {
        const stateFromLocalStorage = localStorage?.getItem("dataGridState");
        setInitialState(stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {});

        // Handle refresh and navigating away/refreshing
        window.addEventListener("beforeunload", saveSnapshot);

        return () => {
            // In case of a SPA remove the event-listener
            window.removeEventListener("beforeunload", saveSnapshot);
            saveSnapshot();
        };
    }, [saveSnapshot]);

    useEffect(() => {
        if (store.getState().dataTables[tableName] !== undefined) {
            apiRef.current.restoreState(store.getState().dataTables[tableName]);
        }
    }, []);

    const ToolbarContainer = useMemo(() => {
        return (value: any) => (
            <GridToolbarContainer>
                <GridToolbarFilterButton ref={value.setFilterButtonEl} />
                {children}
            </GridToolbarContainer>
        );
    }, [children]);

    return (
        <DataGrid
            // Address an issue where certain data models do not have an internally managed, unique ID.
            getRowId={(row) => (row.hasOwnProperty("id") ? row.id : generateUuidNative())}
            apiRef={apiRef}
            rowSelection={allowRowSelected}
            // @ts-ignore
            experimentalFeatures={{ newEditingApi: true }}
            onCellEditStop={cellEditStop === undefined || cellEditStop === null ? () => {} : cellEditStop}
            onCellEditStart={cellEditStart}
            sx={{
                height: height,
                ...customDataGridScrollbarStyle(),
            }}
            initialState={{
                ...initialState,
                columns: {
                    columnVisibilityModel: {
                        id: false,
                    },
                },
            }}
            rows={data}
            columns={columns}
            components={{
                Toolbar: ToolbarContainer,
            }}
            componentsProps={{
                panel: {
                    anchorEl: filterButtonEl,
                },
                toolbar: {
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                    setFilterButtonEl,
                },
            }}
        />
    );
};

export default DataTable;
